import type { CSSProperties, ReactNode } from 'react';
import { color, radius } from '../../theme/tokens.js';
import { Skeleton } from '../States/States.js';

/**
 * The Audit page's loading states — one per route state, each drawn in the SHAPE of what is
 * coming (owner request 2026-10-14).
 *
 * They replace one 420px grey block. On sign-in the List is uncached and its run discovery takes
 * ~14 s (PRD-Audit §8.9), long enough for a featureless block to read as a page that is broken
 * or switched off. The cards and rows below say "this page, filling in", and they hold roughly
 * the height the content will take, so the page does not jump when it lands.
 *
 * ⚠ The shapes are a silhouette, not a claim. The row counts are fixed and read nothing from
 * data, and nothing here may pass for data either: no digits, no `Clean`/`Pass`, no pill in a
 * cadence or status colour. A placeholder that looks like a verdict is a fabricated verdict
 * (FR-AU17's objection to padding the runs table, arriving on the loading state).
 * ⚠ Every shape is `aria-hidden` and the frame is ONE `role="status"` with a spoken label, so a
 * screen reader hears "Loading audits…" rather than forty unlabelled boxes.
 */

const CARD: CSSProperties = {
  background: color.surface,
  border: `1px solid ${color.border}`,
  borderRadius: radius['6xl'],
  overflow: 'hidden',
};

/** A pill's outline. `flex: none`, or a narrow row squeezes it into a bar. */
const PILL: CSSProperties = { borderRadius: radius.pill, flex: 'none' };

// Varied lengths, cycled by row, so the list reads as text rather than a barcode.
const NAME_WIDTHS = ['36%', '48%', '30%', '42%'] as const;
const SUMMARY_WIDTHS = ['58%', '50%', '66%', '44%'] as const;

function Frame({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="status">
      <span className="sr-only">{label}</span>
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

/** Where `<BackLink>` will sit: same line height and the same 14px below it. */
function BackLinkBone({ width }: { width: number }) {
  return (
    <div style={{ padding: '2px 0', marginBottom: 14 }}>
      <Skeleton height={12} width={width} />
    </div>
  );
}

interface BoneColumn {
  /** The grid track the real column declares, so the bones sit where its figures will. */
  track: string;
  align?: 'left' | 'right';
  header: number;
  /** Cycled by row. */
  cells: ReadonlyArray<number | string>;
  pill?: boolean;
}

/**
 * `<DataTable>`'s frame — the subtle header band, the hairline between rows, the 20px/16px edge
 * gutters — with a bone where each cell's content goes.
 */
function TableBone({ columns, rows }: { columns: readonly BoneColumn[]; rows: number }) {
  const grid = columns.map((c) => c.track).join(' ');
  const edge = (index: number, outer: number, inner: number): CSSProperties => ({
    paddingLeft: index === 0 ? outer : inner,
    paddingRight: index === columns.length - 1 ? outer : inner,
  });
  const justify = (c: BoneColumn) => (c.align === 'right' ? 'flex-end' : 'flex-start');

  return (
    <div style={{ ...CARD, borderRadius: radius['5xl'] }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: grid,
          background: color.surfaceSubtle,
          borderBottom: `1px solid ${color.borderSubtle}`,
        }}
      >
        {columns.map((c, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: justify(c), padding: '11px 0', ...edge(i, 20, 10) }}>
            <Skeleton height={9} width={c.header} />
          </div>
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div
          key={r}
          style={{
            display: 'grid',
            gridTemplateColumns: grid,
            alignItems: 'center',
            borderTop: r === 0 ? undefined : `1px solid ${color.borderSubtle}`,
          }}
        >
          {columns.map((c, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: justify(c),
                padding: c.pill ? '10px 0' : '14px 0',
                ...edge(i, 20, 10),
              }}
            >
              <Skeleton
                height={c.pill ? 20 : 12}
                width={c.cells[r % c.cells.length]}
                style={c.pill ? PILL : undefined}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ---- State 1 — the list ------------------------------------------------------------------------

/** Rows per section card. A silhouette of the catalogue, not a count of it. */
const LIST_SECTIONS = [2, 3, 4] as const;
const SECTION_TITLE_WIDTHS = [190, 150, 120] as const;

function ListRowBone({ n }: { n: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 18px',
        borderTop: `1px solid ${color.borderSubtle}`,
      }}
    >
      <Skeleton height={20} width={60} style={PILL} />
      <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 7 }}>
        <Skeleton height={12} width={NAME_WIDTHS[n % NAME_WIDTHS.length]} />
        <Skeleton height={9} width={SUMMARY_WIDTHS[n % SUMMARY_WIDTHS.length]} />
      </div>
      <div style={{ flex: 'none', display: 'grid', gap: 7, justifyItems: 'end' }}>
        <Skeleton height={11} width={72} />
        <Skeleton height={9} width={104} />
      </div>
      {/* The chevron's slot, left empty: an arrow on a row that cannot be opened yet invites a click. */}
      <div style={{ width: 16, flex: 'none' }} />
    </div>
  );
}

export function AuditListSkeleton() {
  return (
    <Frame label="Loading audits…">
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 18,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 320px', maxWidth: 640, display: 'grid', gap: 8 }}>
          <Skeleton height={11} width="94%" />
          <Skeleton height={11} width="56%" />
        </div>
        {/* The cadence control — four segments. */}
        <Skeleton height={30} width={250} style={{ borderRadius: radius['2xl'], flex: 'none' }} />
      </div>

      {LIST_SECTIONS.map((rows, s) => (
        <div key={s} style={{ marginBottom: 22 }}>
          <div style={{ marginBottom: 10 }}>
            <Skeleton height={10} width={SECTION_TITLE_WIDTHS[s]} />
          </div>
          <div style={CARD}>
            {Array.from({ length: rows }, (_, r) => (
              <ListRowBone key={r} n={s * 4 + r} />
            ))}
          </div>
        </div>
      ))}
    </Frame>
  );
}

// ---- State 2 — one audit's runs ----------------------------------------------------------------

const RUNS_COLUMNS: readonly BoneColumn[] = [
  { track: '1.1fr', header: 56, cells: [92, 88, 96] },
  { track: '2.2fr', header: 60, cells: ['72%', '64%', '80%', '58%'] },
  { track: '.9fr', align: 'right', header: 48, cells: [40, 32, 44] },
];

export function AuditRunsSkeleton() {
  return (
    <Frame label="Loading this audit’s runs…">
      <BackLinkBone width={86} />
      <div style={{ ...CARD, padding: '16px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Skeleton height={20} width={60} style={PILL} />
          <Skeleton height={9} width={170} />
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          <Skeleton height={11} width="88%" />
          <Skeleton height={11} width="52%" />
        </div>
      </div>
      <TableBone columns={RUNS_COLUMNS} rows={6} />
    </Frame>
  );
}

// ---- State 3 — one run's results ---------------------------------------------------------------

const RESULTS_COLUMNS: readonly BoneColumn[] = [
  { track: '1.6fr', header: 64, cells: ['48%', '62%', '40%', '56%', '44%'] },
  { track: '.6fr', header: 44, cells: [34], pill: true },
  { track: '1.1fr', align: 'right', header: 52, cells: [34, 28, 40] },
  { track: '1.1fr', align: 'right', header: 52, cells: [40, 30, 36] },
  { track: '.8fr', align: 'right', header: 44, cells: [52], pill: true },
];

export function AuditResultsSkeleton() {
  return (
    <Frame label="Loading this run’s results…">
      <BackLinkBone width={150} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <div style={{ ...CARD, flex: 1, padding: '16px 20px', display: 'grid', gap: 9 }}>
          <Skeleton height={13} width={180} />
          <Skeleton height={10} width="46%" />
        </div>
        {/* Previous / next run. */}
        <Skeleton height={39} width={88} style={{ borderRadius: radius['2xl'], flex: 'none' }} />
      </div>
      <TableBone columns={RESULTS_COLUMNS} rows={8} />
    </Frame>
  );
}
